import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { useI18n } from "../../contexts/I18nContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";
import { FileText, RotateCcw, Save } from "lucide-react";

/** Editable SMS/WhatsApp message templates (absence / late / grades / fees). */
export default function MessageTemplatesTab() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const [items, setItems] = useState([]);
  const [busyKey, setBusyKey] = useState(null);

  const PH_LABELS = {
    student_name: t("mt_ph_student_name"),
    date: t("mt_ph_date"),
    class: t("mt_ph_class"),
    school_name: t("mt_ph_school_name"),
    score: t("mt_ph_score"),
    max_score: t("mt_ph_max_score"),
    subject: t("mt_ph_subject"),
    assessment: t("mt_ph_assessment"),
    amount: t("mt_ph_amount"),
    due_date: t("mt_ph_due_date"),
  };

  // Translate the backend-provided template name using the i18n key `mt_tpl_<key>` if available.
  const tplName = (tpl) => {
    const key = `mt_tpl_${tpl.key}`;
    const translated = t(key);
    // Fallback to name_ar when arabic OR when no translation key exists (t returns key on miss)
    if (translated && translated !== key) return translated;
    return lang === "ar" ? (tpl.name_ar || tpl.key) : (tpl.name || tpl.name_ar || tpl.key);
  };

  const load = () => api.get("/message-templates").then(r => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const setBody = (key, body) => setItems(arr => arr.map(x => x.key === key ? { ...x, body } : x));

  const save = async (tpl) => {
    setBusyKey(tpl.key);
    try {
      await api.put(`/message-templates/${tpl.key}`, { school_id: user.school_id || "", body: tpl.body });
      toast.success(`${t("mt_template_saved") || "Template saved"}: ${tplName(tpl)}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("mt_save_error") || "Error saving"); }
    finally { setBusyKey(null); }
  };

  const reset = async (tpl) => {
    setBusyKey(tpl.key);
    try {
      await api.post(`/message-templates/${tpl.key}/reset`, { school_id: user.school_id || "" });
      toast.success(t("mt_reset_done") || "Reset to default");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || t("mt_save_error") || "Error"); }
    finally { setBusyKey(null); }
  };

  const insertPh = (key, ph) => setItems(arr => arr.map(x => x.key === key ? { ...x, body: `${x.body} {${ph}}` } : x));

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        {t("mt_hint") || "These templates are used automatically in SMS / WhatsApp / email sent to parents. Click any placeholder to insert it — it will be replaced with the real value on send."}
      </p>
      {items.map(tpl => (
        <Card key={tpl.key} className="p-4 border" data-testid={`template-${tpl.key}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="font-semibold flex items-center gap-2">
              <FileText className="w-4 h-4 text-emerald-600" />{tplName(tpl)}
              {tpl.is_custom && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800">{t("mt_custom") || "Custom"}</span>}
            </div>
            <div className="flex gap-2">
              {tpl.is_custom && (
                <Button size="sm" variant="ghost" onClick={() => reset(tpl)} disabled={busyKey === tpl.key} data-testid={`template-reset-${tpl.key}`}>
                  <RotateCcw className="w-3.5 h-3.5 me-1" />{t("mt_default") || "Default"}
                </Button>
              )}
              <Button size="sm" onClick={() => save(tpl)} disabled={busyKey === tpl.key} data-testid={`template-save-${tpl.key}`}>
                <Save className="w-3.5 h-3.5 me-1" />{t("save")}
              </Button>
            </div>
          </div>
          <Textarea rows={3} value={tpl.body} onChange={e => setBody(tpl.key, e.target.value)} data-testid={`template-body-${tpl.key}`} />
          <div className="flex flex-wrap gap-1.5 mt-2">
            {tpl.placeholders.map(ph => (
              <button key={ph} type="button" onClick={() => insertPh(tpl.key, ph)}
                className="text-[11px] px-2 py-0.5 rounded-full border bg-muted/40 hover:bg-emerald-50 hover:border-emerald-400 transition-colors font-mono"
                title={PH_LABELS[ph] || ph}>
                {"{" + ph + "}"} · {PH_LABELS[ph] || ph}
              </button>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}
